import {
  AccountAmountField,
  AccountAmountValue,
  Box,
  BrightyStepButton,
  Button,
  ButtonLink,
  ContentIsland,
  FormContentIsland,
  Grid,
  Stack,
  Step,
  Stepper,
  TransferBreakdown,
  UiFee,
  VaultListItemContent,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '../../Layout/MainLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccountsWithAccountQuery } from '../../../api/accounts';
import { ApiAccount, ApiVault } from '../../../openapi';
import {
  mapClientMoneyToServerMoney,
  parseFloatValue,
  serverAmountToClientAmount,
} from '@razz-team/utils';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { TransferComplete } from '../../TransferComplete/TransferComplete';
import { Form } from '@razz-team/ui-web/src/components/Form/Form';
import { Route } from '../../../routes/account.vaults_.withdraw';
import {
  useVaultsQuery,
  useVaultWithdrawIntentMutation,
  useVaultWithdrawMutation,
} from '../../../api/vaults';
import { detectToWithdrawAccount, detectTransferVault } from './utils';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';

type Directions = {
  from: AccountAmountValue<ApiVault> | undefined;
  to: AccountAmountValue<ApiAccount> | undefined;
};

export function VaultWithdrawPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const vaultsQuery = useVaultsQuery();
  const accountsQuery = useAccountsWithAccountQuery(search.accountId);

  const intentMutation = useVaultWithdrawIntentMutation();
  const { mutate: mutateIntent, data: intent } = intentMutation;
  const idempotencyKey = useIdempotencyKey();
  const withdrawMutation = useVaultWithdrawMutation({ idempotencyKey });

  const [directions, setDirections] = useState<Directions>(() => ({
    from: undefined,
    to: undefined,
  }));

  const vaults = useMemo(() => vaultsQuery.data ?? [], [vaultsQuery.data]);
  const accounts = useMemo(
    () =>
      (accountsQuery.data ?? []).filter(
        (it) => it.balance.currency === directions.from?.account.balance.currency,
      ),
    [accountsQuery.data, directions.from?.account.balance.currency],
  );

  useEffect(() => {
    setDirections((prev): Directions => {
      if (prev.from && prev.to) return prev;
      const from = detectTransferVault({
        vaults,
        defaultVaultId: search.vaultId,
      });
      const to = detectToWithdrawAccount({
        accounts,
        defaultAccountId: search.accountId,
        vault: from,
      });

      return {
        from: from ? { clientAmount: '', account: from } : undefined,
        to: to ? { clientAmount: '', account: to } : undefined,
      };
    });
  }, [search.vaultId, search.accountId, accounts, vaults]);

  const handleChangeFrom = useCallback(
    (nextFrom: AccountAmountValue<ApiVault>) => {
      setDirections(({ to }) => {
        if (to) {
          const amount = mapClientMoneyToServerMoney({
            amount: parseFloatValue(nextFrom.clientAmount),
            currency: nextFrom.account.balance.currency,
          });
          mutateIntent({
            side: 'SELL',
            amount,
            sourceCurrency: amount.currency,
            targetCurrency: to.account.balance.currency,
            sourceVaultId: nextFrom.account.id,
            targetAccountId: to.account.id,
          });
        }

        return { to, from: nextFrom };
      });
    },
    [mutateIntent],
  );

  const handleChangeTo = useCallback(
    (nextTo: AccountAmountValue<ApiAccount>) => {
      setDirections(({ from }) => {
        if (from) {
          const amount = mapClientMoneyToServerMoney({
            amount: parseFloatValue(nextTo.clientAmount),
            currency: nextTo.account.balance.currency,
          });
          mutateIntent({
            side: 'BUY',
            amount,
            sourceCurrency: from.account.balance.currency,
            targetCurrency: amount.currency,
            sourceVaultId: from.account.id,
            targetAccountId: nextTo.account.id,
          });
        }

        return { from, to: nextTo };
      });
    },
    [mutateIntent],
  );

  useEffect(() => {
    if (!intent) return;
    if (intent.quote.fx) {
      const {
        amount,
        quote: { targetAmount },
      } = intent;
      const fx = intent.quote.fx;
      setDirections((prev) => {
        if (fx.side === 'SELL' && prev.from && prev.to) {
          return {
            from: prev.from,
            to: {
              account: prev.to.account,
              clientAmount: String(serverAmountToClientAmount(targetAmount)),
            },
          };
        }

        if (fx.side === 'BUY' && prev.from && prev.to) {
          return {
            to: prev.to,
            from: {
              account: prev.from.account,
              clientAmount: String(serverAmountToClientAmount(amount)),
            },
          };
        }

        return prev;
      });
    } else {
      // support sending between accounts with the same currency
      setDirections((prev) => {
        const { to, from } = prev;
        if (
          to &&
          from &&
          to.account.balance.currency === from.account.balance.currency &&
          to.account.balance.currency === intent.quote.targetAmount.currency &&
          from.account.balance.currency === intent.quote.sourceAmount.currency
        ) {
          return {
            from: {
              account: from.account,
              clientAmount: String(serverAmountToClientAmount(intent.quote.sourceAmount)),
            },
            to: {
              account: to.account,
              clientAmount: String(serverAmountToClientAmount(intent.quote.targetAmount)),
            },
          };
        }

        return prev;
      });
    }
  }, [intent]);

  const [step, setStep] = useState<'amount' | 'confirm'>('amount');
  const backToEdit = useCallback(() => setStep('amount'), []);

  const handleSubmit = useCallback(() => {
    if (step === 'amount') {
      setStep('confirm');
    } else if (withdrawMutation.isIdle && intentMutation.data && directions.from && directions.to) {
      withdrawMutation.mutate({
        hash: intentMutation.data.hash,
        fees: intentMutation.data.fees,
        quote: intentMutation.data.quote,
        targetAccountId: directions.to.account.id,
        sourceVaultId: directions.from.account.id,
      });
    }
  }, [step, directions, intentMutation.data, withdrawMutation]);

  useEffect(() => {
    if (withdrawMutation.isSuccess) {
      setStep('confirm');
    }
  }, [withdrawMutation.isSuccess]);

  const uiFees = useMemo(() => {
    return intentMutation.data?.fees
      ? Object.entries(intentMutation.data?.fees ?? {}).reduce((acc, [type, fee]) => {
          if (fee?.sourceAmount) {
            acc.push({
              type,
              value: fee.sourceAmount,
            });
          }
          return acc;
        }, [] as UiFee[])
      : null;
  }, [intentMutation.data?.fees]);

  const uiRate = useMemo(() => {
    if (intentMutation.data) {
      const { fx } = intentMutation.data.quote;
      if (fx) {
        return {
          price: fx.side === 'BUY' ? fx.rate.ask : fx.rate.bid,
          currencyPair: fx.rate.currencyPair,
        };
      }

      return undefined;
    }

    return null;
  }, [intentMutation.data]);

  const fromTitle = useMemo(() => {
    const vault = directions.from?.account;
    if (!vault) return t('From');

    const accountName = t('Vault {{currency}}', { currency: vault.balance.currency });
    return t('From {{accountName}}', { accountName });
  }, [directions.from?.account, t]);

  const toTitle = useMemo(() => {
    const account = directions.to?.account;
    if (!account) return t('To');

    const accountName =
      account.name ?? t('Account {{currency}}', { currency: account.balance.currency });
    return t('To {{accountName}}', { accountName });
  }, [directions.to?.account, t]);

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Withdraw')}
          parent={{
            to: '/account/vaults',
            title: t('Earning vaults'),
          }}
        />
      }
      compact
    >
      <Grid size={12}>
        <Box sx={{ width: '100%' }}>
          <Stepper alternativeLabel activeStep={step === 'confirm' ? 1 : 0}>
            <Step active={step === 'amount'}>
              <BrightyStepButton onClick={backToEdit}>{t('Amount')}</BrightyStepButton>
            </Step>
            <Step active={step === 'confirm'}>
              <BrightyStepButton>{t('Confirm')}</BrightyStepButton>
            </Step>
          </Stepper>
        </Box>
      </Grid>
      <Grid
        component={Form}
        container
        display="flex"
        justifyContent="center"
        onSubmit={handleSubmit}
        size={12}
      >
        <Stack direction="column" width="100%" maxWidth={500} spacing={6} alignItems="stretch">
          {withdrawMutation.data?.transaction ? (
            <TransferComplete transaction={withdrawMutation.data?.transaction} />
          ) : (
            <>
              {step === 'amount' ? (
                <FormContentIsland title={fromTitle}>
                  <AccountAmountField<ApiVault>
                    value={directions.from}
                    options={vaults}
                    onChange={handleChangeFrom}
                    autoFocus
                    helperSellAll
                    sellAllWord={t('Withdraw')}
                    validateBalance
                    renderItemContent={(vault) => <VaultListItemContent vault={vault} />}
                  />
                </FormContentIsland>
              ) : null}

              <ContentIsland
                title={step === 'confirm' ? t('Withdrawal details') : undefined}
                actions={
                  step === 'confirm' ? (
                    <Button variant="text" color="info" onClick={backToEdit}>
                      {t('Edit')}
                    </Button>
                  ) : undefined
                }
              >
                <TransferBreakdown
                  variant={step === 'confirm' ? 'confirm' : 'form'}
                  side={intentMutation.data?.quote.fx?.side}
                  amountToDebit={step === 'confirm' ? intentMutation.data?.amount : undefined}
                  amountToConvert={intentMutation.data?.quote.sourceAmount ?? null}
                  fees={uiFees}
                  rate={uiRate}
                  amountToTransfer={
                    step === 'confirm' ? intentMutation.data?.quote.targetAmount : undefined
                  }
                  error={withdrawMutation.error ?? intentMutation.error}
                />
              </ContentIsland>
              {step === 'amount' ? (
                <FormContentIsland title={toTitle}>
                  <AccountAmountField<ApiAccount>
                    value={directions.to}
                    options={accounts}
                    onChange={handleChangeTo}
                  />
                </FormContentIsland>
              ) : null}
            </>
          )}
          {withdrawMutation.data?.transaction ? (
            <ButtonLink
              size="large"
              color="primary"
              to="/account/vaults/$vaultId"
              params={{ vaultId: withdrawMutation.variables.sourceVaultId }}
              fullWidth
            >
              {t('Done')}
            </ButtonLink>
          ) : (
            <Button
              type="submit"
              size="large"
              color="primary"
              loading={intentMutation.isPending || withdrawMutation.isPending}
              disabled={!intentMutation.isSuccess}
              fullWidth
            >
              {step === 'confirm' ? t('Confirm') : t('Review order')}
            </Button>
          )}
        </Stack>
      </Grid>
    </MainLayout>
  );
}
